import * as React from 'react';
import * as Urql from 'urql';
import { normalizeGraphQlError } from '../graphql/normalize-error.js';
import { getSession, createSessionMutation, createPlayerMutation, destroySessionMutation } from './queries.js';

/**
 *  @name useSession
 *
 *  This hook returns session object and
 *  methods to create (player or session), destroy and refetch session.
 *
 *
 * @return {object}
 * @access public
 *
 * @example
 *
 *  function App(){
 *  return(
 *    <Herz.GraphQL.Provider client={client}>
 *      <Component />
 *    </Herz.GraphQL.Provider>
 *  )
 * }
 *
 *
 * function Component(){
 *   const session = Herz.Auth.useSession()
 *   const [err, setErr] = React.useState(null)
 *
 *    function handleLogin(){
 *       const created = session.create({username: "user", password: "password"})
 *       if (created.error) {
 *          setErr(created.error)
 *       }
 *    }
 *
 *    function handleRegister(){
 *       const created = session.create({
 *         username: "user",
 *         password: "password",
 *         passwordConfirmation:"password",
 *         country:"Sweden",
 *         firstName:"Karin",
 *         secondName:"Tidbeck"
 *        })
 *
 *        if (created.error) {
 *          setErr(created.error)
 *        }
 *    }
 *
 *    function handleLogout(){
 *        session.destroy()
 *    }
 *
 *    if(session.authenticated){
 *      return <div><button onClick={handleLogout}>Login</button><button onClick={handleRegister}>Register</button></div>
 *    }
 *
 *    if(sesstion.fetching){
 *      return <div>Loading...</div>
 *    }
 *
 *    return(
 *      <div>
 *        <button onClick={handleLogin}>Login</button>
 *        <div>or</div>
 *        <button onClick={handleRegister}>Register</button>
 *      </div>
 *    )
 * }
 */

/**
 * @typedef {object} Input
 */

function useSession() {
  var _response$data$player, _response$data, _response$data$player2;

  var [response, reexecuteQuery] = Urql.useQuery({
    query: getSession
  });
  var [, createSession] = Urql.useMutation(createSessionMutation);
  var [, createPlayer] = Urql.useMutation(createPlayerMutation);
  var [, destroySession] = Urql.useMutation(destroySessionMutation);
  var session = (_response$data$player = (_response$data = response.data) === null || _response$data === void 0 ? void 0 : (_response$data$player2 = _response$data.player) === null || _response$data$player2 === void 0 ? void 0 : _response$data$player2.session) !== null && _response$data$player !== void 0 ? _response$data$player : {};
  /**
   *
   *  Method "create" can be used for both creating session
   *  and creating player, relying on the input object.
   *  If input object contains passwordConfirmation key, createPlayer
   *  mutation will be executed, otherwise 'session' will be created instead.
   *
   * @name create
   * @param {Input | CreatePlayerInput}
   * @access public
   * */

  var create = React.useCallback(input => {
    // TODO: Make "createPlayer" method to be part of the player management namespace
    if (input.passwordConfirmation) {
      // Create player
      return createPlayer({
        input
      }).then(res => {
        var _res$data;

        if (res.error) {
          var normalizedError = normalizeGraphQlError(res.error);
          return {
            error: normalizedError
          };
        }

        return (_res$data = res === null || res === void 0 ? void 0 : res.data) !== null && _res$data !== void 0 ? _res$data : {};
      });
    } // Create session


    return createSession(input).then(res => {
      var _res$data2;

      if (res.error) {
        var normalizedError = normalizeGraphQlError(res.error);
        return {
          error: normalizedError
        };
      }

      return (_res$data2 = res === null || res === void 0 ? void 0 : res.data) !== null && _res$data2 !== void 0 ? _res$data2 : {};
    });
  }, [createSession, createPlayer]);
  /**
   * Destroy session callback
   * @name destroy
   * @access public
   */

  var destroy = React.useCallback(() => {
    return destroySession();
  }, [destroySession]);
  /**
   * Invalidate session callback
   * @name invalidate
   * @access public
   */

  function invalidateSession() {
    reexecuteQuery({
      requestPolicy: "cache-and-network"
    });
  }

  return {
    /**
     * Authentication status
     *
     * @type {boolean}
     */
    authenticated: Boolean(session === null || session === void 0 ? void 0 : session.authenticated),

    /**
     * Session creation datetime
     */
    createdAt: session === null || session === void 0 ? void 0 : session.createdAt,

    /**
     * Session token
     *
     * @type {string}
     */
    token: session === null || session === void 0 ? void 0 : session.token,

    /**
     * Prefixed session token
     *
     * Required to authenticate
     * player at fasttrack. This is a workaround
     * to allow BE verify which brand is being authenticated
     * since we have only a single brand integration.
     *
     * @type {string}
     */
    prefixedToken: session === null || session === void 0 ? void 0 : session.prefixedToken,

    /**
     * Create player/session
     *
     * Performs either mutation based on input. See GraphQL mutations docs.
     */
    create,

    /**
     * Terminates current session
     */
    destroy,

    /**
     * Marks current session data as stale and forces refetching
     */
    invalidate: invalidateSession,

    /**
     * Error
     * @type {object}
     */
    error: response.error,

    /**
     * Session update status
     *
     * @type {boolean}
     */
    fetching: response.fetching
  };
} // #endregion

export { useSession };
